import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI417_2 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    nonRegulations: {
      reportingPeriod: "",
      fineOrpenalty: "",
      resultingWarning: "",
      voluntarCodes: "",
    },

    noNonComplianceProductInfo: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        const data = response.data.data.GRI400?.nonComplianceConcering || {};
        setFormData({
          nonRegulations: {
            reportingPeriod: data.nonRegulations?.reportingPeriod || "",
            fineOrpenalty: data.nonRegulations?.fineOrpenalty || "",
            voluntarCodes: data.nonRegulations?.voluntarCodes || "",
            resultingWarning: data.nonRegulations?.resultingWarning || "",
          },
          noNonComplianceProductInfo: data.noNonComplianceProductInfo || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData

    if (!formData.nonRegulations.reportingPeriod) {
      newErrors.reportingPeriod = "This field is required";
      formIsValid = false;
    }

    if (!formData.nonRegulations.fineOrpenalty) {
      newErrors.fineOrpenalty = "This field is required";
      formIsValid = false;
    }

    if (!formData.nonRegulations.resultingWarning) {
      newErrors.resultingWarning = "This field is required";
      formIsValid = false;
    }

    if (!formData.nonRegulations.voluntarCodes) {
      newErrors.voluntarCodes = "This field is required";
      formIsValid = false;
    }

    if (!formData.noNonComplianceProductInfo) {
      newErrors.noNonComplianceProductInfo = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return; // Validate form before submission

      const payload = {
        type: "GRI400",
        submitted417_2: true,
        nonComplianceConcering: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/417-3/${id}`);
      } catch (error) {
        console.error("Error updating data:", error);
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/417-3/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p className={styles.section_heading}>417 : Marketing & Labelling</p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        417-2 : Incidents of non-compliance concerning product and service
        information and labelling
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <SectionHeading text="a. How many incidents of non-compliance with regulations and/or voluntary codes regarding product and service information and labeling occurred within the reporting period? Please provide the following breakdown:" />
        <CustomTextField
          value={formData.nonRegulations.reportingPeriod}
          onChange={(e) =>
            handleNestedInputChange(
              "nonRegulations",
              "reportingPeriod",
              e.target.value
            )
          }
          error={errors.reportingPeriod}
          helperText={errors.reportingPeriod}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i.  Incidents of non-compliance with regulations resulting in a fine or penalty." />
          <CustomTextField
            value={formData.nonRegulations.fineOrpenalty}
            onChange={(e) =>
              handleNestedInputChange(
                "nonRegulations",
                "fineOrpenalty",
                e.target.value
              )
            }
            error={errors.fineOrpenalty}
            helperText={errors.fineOrpenalty}
            disabled={disabled}
          />

          <SectionHeading text="ii. Incidents of non-compliance with regulations resulting in a warning." />
          <CustomTextField
            value={formData.nonRegulations.resultingWarning}
            onChange={(e) =>
              handleNestedInputChange(
                "nonRegulations",
                "resultingWarning",
                e.target.value
              )
            }
            error={errors.resultingWarning}
            helperText={errors.resultingWarning}
            disabled={disabled}
          />

          <SectionHeading text="iii. Incidents of non-compliance with voluntary codes." />
          <CustomTextField
            value={formData.nonRegulations.voluntarCodes}
            onChange={(e) =>
              handleNestedInputChange(
                "nonRegulations",
                "voluntarCodes",
                e.target.value
              )
            }
            error={errors.voluntarCodes}
            helperText={errors.voluntarCodes}
            disabled={disabled}
          />
        </Box>

        <SectionHeading
          text="b. If the organization did not identify any instances of non-compliance with regulations and/or voluntary codes regarding product and service information and labeling, a brief statement confirming this fact is sufficient."
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.noNonComplianceProductInfo}
          onChange={(e) =>
            handleInputChange("noNonComplianceProductInfo", e.target.value)
          }
          placeholder={"Type Here"}
          error={errors.noNonComplianceProductInfo}
          helperText={errors.noNonComplianceProductInfo}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI417_2;
